/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import type { LinkProps } from '@coursera/cds-core';
import { Link, Typography2, breakpoints } from '@coursera/cds-core';
import { HelpIcon } from '@coursera/cds-icons';

import withSingleTracked from 'bundles/common/components/withSingleTracked';
import { buildLearnerHelpUrl } from 'bundles/common/utils/urlUtils';
import { useDegreeDescriptionEventingContext } from 'bundles/degree-description/providers/DegreeDescriptionEventingProvider';

import _t from 'i18n!nls/degree-description';

const styles = {
  container: css`
    background-color: var(--cds-color-neutral-background-primary);
    padding: var(--cds-spacing-400);
    border: 2px solid var(--cds-color-neutral-stroke-primary-weak);
    border-radius: 8px;
    ${breakpoints.down('md')} {
      padding: var(--cds-spacing-200);
    }
  `,
  defaultHelpCenter: css`
    display: flex;
    flex-direction: column;
    ${breakpoints.down('md')} {
      flex-direction: row;
    }
  `,
  helpCenterIconContainer: css`
    color: var(--cds-color-neutral-primary);
    ${breakpoints.down('md')} {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      margin-right: var(--cds-spacing-200);
    }
  `,
  helpCenterLink: css`
    display: inline-block;
    margin-top: var(--cds-spacing-150);
  `,
};

const TrackedLink = withSingleTracked({ type: 'BUTTON' })<LinkProps>(Link);

export const HelpCenterBlock = () => {
  const { trackClickButton } = useDegreeDescriptionEventingContext();
  const helpUrl = buildLearnerHelpUrl();

  const handleOnClick = () => {
    trackClickButton('degree_info_link', 'faq', helpUrl, _t('Visit the learner help center'));
  };

  return (
    <div css={[styles.container, styles.defaultHelpCenter]}>
      <div css={styles.helpCenterIconContainer}>
        <HelpIcon size="large" />
      </div>
      <div>
        <Typography2 variant="subtitleLarge" component="h3">
          {_t('More questions?')}
        </Typography2>
        <TrackedLink
          href={helpUrl}
          variant="quiet"
          typographyVariant="subtitleMedium"
          trackingName="faqSection_learnerHelpCenterLink"
          onClick={handleOnClick}
          css={styles.helpCenterLink}
        >
          {_t('Visit the learner help center')}
        </TrackedLink>
      </div>
    </div>
  );
};

export default HelpCenterBlock;
